import React, { memo, useCallback, useEffect, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { fetchArts } from 'api/art';
import { fetchManufacturers } from 'api/manufacturer';
import { fetchWashHubLocations } from 'api/location';
import { fetchWashHubAdminsForLocation } from 'api/employees';
import {
  deletePurchaseOrder,
  sendPurchaseOrder,
  updatePurchaseOrderCosts,
} from 'api/purchaseOrder';
import { fetchSkusByManufacturer } from 'api/sku';
import { useUser } from 'store/slices/auth/authSelectors';
import {
  SendPurchaseOrderPayload,
  UpdatePurchaseOrderCostsPayload,
} from 'types/purchaseOrder';

import { updatePurchaseOrderFromForm } from '../CreatePurchaseOrderModal/createPurchaseOrder';
import {
  CreatePurchaseOrderFormValues,
  getShipToContactDetails,
} from '../CreatePurchaseOrderModal/CreatePurchaseOrderModal.utils';
import { PurchaseOrderDetailsModalProps } from './PurchaseOrderDetailsModal.props';
import PurchaseOrderDetailsModalView from './PurchaseOrderDetailsModal.view';

const PurchaseOrderDetailsModalContainer = ({
  isOpen,
  onClose,
  purchaseOrder,
  onViewAttachments,
  onPurchaseOrderUpdated,
  onMarkAsReceived,
  onPayVendor,
}: PurchaseOrderDetailsModalProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const currentUser = useUser() as {
    id?: string;
    _id?: string;
    userId?: string;
    user?: { id?: string; _id?: string; userId?: string };
  };
  const [selectedWashHubId, setSelectedWashHubId] = useState('');
  const manufacturerId = purchaseOrder?.manufacturerId || '';

  useEffect(() => {
    if (!isOpen) {
      setSelectedWashHubId('');
      return;
    }

    setSelectedWashHubId(purchaseOrder?.destinationLocationId || '');
  }, [isOpen, purchaseOrder?._id, purchaseOrder?.destinationLocationId]);

  const {
    data: manufacturers = [],
    isLoading: isLoadingManufacturers,
  } = useQuery(['fetch-manufacturers'], () => fetchManufacturers(), {
    enabled: isOpen,
  });

  const { data: washHubs = [], isLoading: isLoadingWashHubs } = useQuery(
    ['fetch-wash-hub-locations'],
    fetchWashHubLocations,
    { enabled: isOpen },
  );

  const { data: arts = [], isLoading: isLoadingArts } = useQuery(
    ['fetch-arts'],
    fetchArts,
    { enabled: isOpen },
  );

  const { data: skus = [], isLoading: isLoadingSkus } = useQuery(
    ['fetch-skus-by-manufacturer', manufacturerId],
    () => fetchSkusByManufacturer(manufacturerId),
    {
      enabled: isOpen && Boolean(manufacturerId),
      refetchOnMount: 'always',
    },
  );

  const {
    data: washHubAdmins = [],
    isLoading: isLoadingWashHubAdmins,
  } = useQuery(
    ['fetch-wash-hub-admins', selectedWashHubId],
    () => fetchWashHubAdminsForLocation(selectedWashHubId),
    { enabled: isOpen && Boolean(selectedWashHubId) },
  );

  const {
    mutateAsync: savePurchaseOrder,
    isLoading: isSavingPurchaseOrder,
  } = useMutation(
    async (values: CreatePurchaseOrderFormValues) => {
      if (!purchaseOrder?._id) {
        throw new Error('Purchase order is missing.');
      }

      const manufacturer = manufacturers.find(
        (item) => item._id === values.manufacturerId,
      );
      const washHub = washHubs.find(
        (item) => item._id === values.destinationHubId,
      );
      const shipToContactDetails = getShipToContactDetails(
        values,
        washHubAdmins,
      );

      return updatePurchaseOrderFromForm(
        purchaseOrder._id,
        values,
        {
          manufacturerName: manufacturer?.name ?? '',
          destinationHubName: values.isManualDestinationHub
            ? values.manualDestinationHubName
            : washHub?.name ?? '',
          shipToContact: shipToContactDetails.name,
          shipToContactPhone: shipToContactDetails.phone,
          shipToContactEmail: shipToContactDetails.email,
        },
        await fetchSkusByManufacturer(values.manufacturerId),
      );
    },
    {
      onSuccess: (data) => {
        if (data?.item) {
          onPurchaseOrderUpdated?.(data.item);
        }

        queryClient.invalidateQueries(['fetch-purchase-orders']);
        toast({
          status: 'success',
          title: 'Purchase order updated',
          description: 'A new UPDATED PDF was added to attachments.',
          isClosable: true,
          duration: 4000,
        });
      },
      onError: (error: Error) => {
        toast({
          status: 'error',
          title: 'Failed to update purchase order',
          description: error?.message || 'Something went wrong.',
          isClosable: true,
          duration: 5000,
        });
      },
    },
  );

  const {
    mutateAsync: sendCurrentPurchaseOrder,
    isLoading: isSendingPurchaseOrder,
  } = useMutation(
    async (payload: SendPurchaseOrderPayload) => {
      if (!purchaseOrder?._id) {
        throw new Error('Purchase order is missing.');
      }

      return sendPurchaseOrder(purchaseOrder._id, payload);
    },
    {
      onSuccess: (data) => {
        if (data?.item) {
          onPurchaseOrderUpdated?.(data.item);
        }

        queryClient.invalidateQueries(['fetch-purchase-orders']);
        toast({
          status: 'success',
          title: 'Purchase order sent',
          isClosable: true,
          duration: 4000,
        });
      },
      onError: (error: Error) => {
        toast({
          status: 'error',
          title: 'Failed to send purchase order',
          description: error?.message || 'Something went wrong.',
          isClosable: true,
          duration: 5000,
        });
      },
    },
  );

  const handleSavePurchaseOrder = useCallback(
    async (values: CreatePurchaseOrderFormValues) => {
      await savePurchaseOrder(values);
    },
    [savePurchaseOrder],
  );

  const handleSendPurchaseOrder = useCallback(
    async (payload: SendPurchaseOrderPayload) => {
      await sendCurrentPurchaseOrder(payload);
    },
    [sendCurrentPurchaseOrder],
  );

  const {
    mutateAsync: savePurchaseOrderCosts,
    isLoading: isUpdatingPurchaseOrderCosts,
  } = useMutation(
    async (payload: UpdatePurchaseOrderCostsPayload) => {
      if (!purchaseOrder?._id) {
        throw new Error('Purchase order is missing.');
      }

      return updatePurchaseOrderCosts(purchaseOrder._id, payload);
    },
    {
      onSuccess: (data) => {
        if (data?.item) {
          onPurchaseOrderUpdated?.(data.item);
        }

        queryClient.invalidateQueries(['fetch-purchase-orders']);
        toast({
          status: 'success',
          title: 'Costs updated',
          isClosable: true,
          duration: 4000,
        });
      },
      onError: (error: Error) => {
        toast({
          status: 'error',
          title: 'Failed to update costs',
          description: error?.message || 'Something went wrong.',
          isClosable: true,
          duration: 5000,
        });
      },
    },
  );

  const handleUpdatePurchaseOrderCosts = useCallback(
    async (payload: UpdatePurchaseOrderCostsPayload) => {
      await savePurchaseOrderCosts(payload);
    },
    [savePurchaseOrderCosts],
  );

  const getCurrentUserId = () => currentUser?.id || '';

  const {
    mutateAsync: removePurchaseOrder,
    isLoading: isDeletingPurchaseOrder,
  } = useMutation(
    async (reasonForDeletion: string) => {
      const poNumber = purchaseOrder?.poNumer;
      const userId = getCurrentUserId();
      if (!poNumber) {
        throw new Error('Purchase order number is missing.');
      }

      if (!userId) {
        throw new Error('User id is missing.');
      }

      return deletePurchaseOrder({
        poNumber,
        userId,
        reasonForDeletion,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fetch-purchase-orders']);
        toast({
          status: 'success',
          title: 'Purchase order deleted',
          isClosable: true,
          duration: 4000,
        });
        onClose();
      },
      onError: (error: Error) => {
        toast({
          status: 'error',
          title: 'Failed to delete purchase order',
          description: error?.message || 'Something went wrong.',
          isClosable: true,
          duration: 5000,
        });
      },
    },
  );

  const handleDeletePurchaseOrder = useCallback(
    async (reasonForDeletion: string) => {
      await removePurchaseOrder(reasonForDeletion);
    },
    [removePurchaseOrder],
  );

  return (
    <PurchaseOrderDetailsModalView
      isOpen={isOpen}
      onClose={onClose}
      purchaseOrder={purchaseOrder}
      onViewAttachments={onViewAttachments}
      manufacturers={manufacturers}
      washHubs={washHubs}
      arts={arts}
      skus={skus}
      washHubAdmins={washHubAdmins}
      isLoadingManufacturers={isLoadingManufacturers}
      isLoadingWashHubs={isLoadingWashHubs}
      isLoadingArts={isLoadingArts}
      isLoadingSkus={isLoadingSkus}
      isLoadingWashHubAdmins={isLoadingWashHubAdmins}
      onDestinationHubChange={setSelectedWashHubId}
      onSavePurchaseOrder={handleSavePurchaseOrder}
      isSavingPurchaseOrder={isSavingPurchaseOrder}
      onSendPurchaseOrder={handleSendPurchaseOrder}
      isSendingPurchaseOrder={isSendingPurchaseOrder}
      onUpdatePurchaseOrderCosts={handleUpdatePurchaseOrderCosts}
      isUpdatingPurchaseOrderCosts={isUpdatingPurchaseOrderCosts}
      onDeletePurchaseOrder={handleDeletePurchaseOrder}
      isDeletingPurchaseOrder={isDeletingPurchaseOrder}
      onMarkAsReceived={onMarkAsReceived}
      onPayVendor={onPayVendor}
    />
  );
};

export default memo(PurchaseOrderDetailsModalContainer);
